import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Wallet, TrendingUp, IndianRupee, Clock, AlertTriangle, Loader2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  PENDING: "bg-amber-100 text-amber-700",
  PAID: "bg-emerald-100 text-emerald-700",
  DISPUTED: "bg-red-100 text-red-700",
  HELD: "bg-orange-100 text-orange-700",
  SETTLED: "bg-emerald-100 text-emerald-700",
};

function fmt(n) {
  return `\u20B9${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function PartnerEarnings() {
  const { user } = useAuth();
  const token = localStorage.getItem("sendtoo_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const partnerId = user?.partner_id || user?.id;

  const [wallet, setWallet] = useState({ balance: 0, credit_limit: 0, available_balance: 0 });
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const fetchAll = useCallback(async () => {
    if (!partnerId) return;
    setLoading(true);
    try {
      const [ledgerRes, walletRes] = await Promise.all([
        fetch(`${API}/api/v2/partners/${partnerId}/ledger`, { headers }),
        fetch(`${API}/api/v2/partners/${partnerId}/wallet`, { headers }),
      ]);
      const ledger = ledgerRes.ok ? await ledgerRes.json() : [];
      const wData = walletRes.ok ? await walletRes.json() : {};
      const list = Array.isArray(ledger) ? ledger : (ledger.items || []);
      setEntries(list);
      setWallet({
        balance: wData.balance || 0,
        credit_limit: wData.credit_limit || 0,
        available_balance: (wData.balance || 0) + (wData.credit_limit || 0),
      });
    } catch { /* ignore */ }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = entries.filter((e) => {
    if (statusFilter !== "All") {
      if ((e.payout_status || e.status || "").toUpperCase() !== statusFilter.toUpperCase()) return false;
    }
    if (dateFrom && (e.created_at || "") < dateFrom) return false;
    if (dateTo && (e.created_at || "") > dateTo + "T23:59:59") return false;
    return true;
  });

  const sumBy = (pred) =>
    entries.filter(pred).reduce((s, e) => s + (e.commission_amount || e.amount || 0), 0);

  const totalEarned = sumBy(() => true);
  const paidOut = sumBy((e) => ((e.payout_status || e.status) || "").toUpperCase() === "PAID");
  const pending = sumBy((e) => ((e.payout_status || e.status) || "").toUpperCase() === "PENDING");
  const disputed = sumBy((e) => ((e.payout_status || e.status) || "").toUpperCase() === "DISPUTED");

  return (
    <DashboardLayout portalColor="green">
      <div className="space-y-5" data-testid="partner-earnings">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">My Earnings</h1>
          <p className="text-sm text-slate-500">Commission ledger & payout history</p>
        </div>

        {/* WALLET CARD */}
        <div className="bg-gradient-to-r from-emerald-50 to-blue-50 border rounded-xl p-5"
             data-testid="wallet-card">
          <div className="flex items-center gap-2 text-xs text-slate-600 uppercase font-semibold tracking-wider">
            <Wallet className="w-4 h-4" /> Partner Wallet
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3">
            <div>
              <div className="text-xs text-slate-500">Balance</div>
              <div className={`text-3xl font-bold ${wallet.balance >= 0 ? "text-emerald-600" : "text-red-600"}`}
                   data-testid="wallet-balance">
                {fmt(wallet.balance)}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Credit Limit</div>
              <div className="text-2xl font-bold text-slate-700">{fmt(wallet.credit_limit)}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Available</div>
              <div className="text-2xl font-bold text-blue-700">{fmt(wallet.available_balance)}</div>
            </div>
          </div>
        </div>

        {/* SUMMARY CARDS */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard icon={TrendingUp} label="Total Earned" value={fmt(totalEarned)}
                       color="text-emerald-600" testid="sum-earned" />
          <SummaryCard icon={IndianRupee} label="Paid Out" value={fmt(paidOut)}
                       color="text-blue-600" testid="sum-paid" />
          <SummaryCard icon={Clock} label="Pending" value={fmt(pending)}
                       color="text-amber-600" testid="sum-pending" />
          <SummaryCard icon={AlertTriangle} label="Disputed" value={fmt(disputed)}
                       color="text-red-600" testid="sum-disputed" />
        </div>

        {/* FILTERS */}
        <div className="bg-white border rounded-lg p-3 grid grid-cols-2 md:grid-cols-4 gap-2 items-end">
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-semibold">From</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                   className="h-9 text-sm" data-testid="earnings-date-from" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-semibold">To</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                   className="h-9 text-sm" data-testid="earnings-date-to" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-semibold">Payout Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full border border-slate-300 rounded h-9 px-2 text-sm"
                    data-testid="earnings-status">
              <option>All</option>
              <option>PENDING</option>
              <option>PAID</option>
              <option>DISPUTED</option>
              <option>HELD</option>
            </select>
          </div>
        </div>

        {/* EARNINGS TABLE */}
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Date</th>
                  <th className="text-left px-3 py-2 font-semibold">Shipment AWB</th>
                  <th className="text-left px-3 py-2 font-semibold">Action</th>
                  <th className="text-right px-3 py-2 font-semibold">Booking Value</th>
                  <th className="text-right px-3 py-2 font-semibold">Commission %</th>
                  <th className="text-right px-3 py-2 font-semibold">Commission ₹</th>
                  <th className="text-left px-3 py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="py-10 text-center">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
                  </td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={7} className="py-10 text-center text-slate-400">No earnings yet</td></tr>
                ) : (
                  filtered.map((e, i) => {
                    const status = ((e.payout_status || e.status) || "PENDING").toUpperCase();
                    return (
                      <tr key={e.id || e._id || i} className="border-b border-slate-100"
                          data-testid={`earning-row-${i}`}>
                        <td className="px-3 py-2 text-slate-500">
                          {e.created_at ? new Date(e.created_at).toLocaleDateString() : "—"}
                        </td>
                        <td className="px-3 py-2 font-mono text-[11px]">{e.awb || e.shipment_id || "—"}</td>
                        <td className="px-3 py-2">{e.action || e.txn_type || e.description || "—"}</td>
                        <td className="px-3 py-2 text-right">{fmt(e.booking_value || e.base_amount || 0)}</td>
                        <td className="px-3 py-2 text-right">
                          {e.commission_percent != null ? `${e.commission_percent}%` : "—"}
                        </td>
                        <td className="px-3 py-2 text-right font-medium text-emerald-600">
                          {fmt(e.commission_amount || e.amount || 0)}
                        </td>
                        <td className="px-3 py-2">
                          <Badge className={`text-[10px] ${STATUS_BADGE[status] || "bg-slate-100 text-slate-600"}`}>
                            {status}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function SummaryCard({ icon: Icon, label, value, color, testid }) {
  return (
    <div className="bg-white border rounded-lg p-4" data-testid={testid}>
      <div className="text-xs text-slate-500 flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className={`text-xl font-bold mt-1 ${color}`}>{value}</div>
    </div>
  );
}
